import { FormEvent, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { checkWhitelist } from '../services/betaAccess';
import { initiateRegistration, verifyRegistration, resendVerificationCode } from '../services/auth';
import BetaAccessForm from '../components/BetaAccessForm';
import { IconMark, IconGoogle, IconMail } from '../components/Icons';

export default function SignIn() {
  const { googleLogin, setUser, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const isMobile = useIsMobile();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBetaForm, setShowBetaForm] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/profile', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((p) => p - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const whitelistCheck = await checkWhitelist(email);

      if (!whitelistCheck.isWhitelisted) {
        if (whitelistCheck.hasPendingRequest) {
          setError(`Your beta access request is ${whitelistCheck.requestStatus}. We'll notify you once it's reviewed.`);
        } else {
          setShowBetaForm(true);
        }
        setIsSubmitting(false);
        return;
      }

      await initiateRegistration(name, email, password);
      setVerificationStep(true);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setShowBetaForm(true);
      } else if (err.response?.status === 409) {
        setError(t('auth:register.emailExists'));
      } else if (err.response?.status === 502) {
        setError(t('auth:register.emailSendFailed'));
      } else {
        setError(t('auth:register.failed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const userData = await verifyRegistration(email, verificationCode);
      setUser(userData);
      navigate('/profile');
    } catch {
      setError(t('auth:register.verification.invalidCode'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      await resendVerificationCode(email);
      setResendCooldown(60);
    } catch {
      setError(t('auth:register.failed'));
    }
  };

  const handleGoogleLogin = useCallback(
    async (response: any) => {
      setIsSubmitting(true);
      setError('');
      try {
        await googleLogin(response.credential);
        navigate('/profile');
      } catch (error: any) {
        if (error.response?.status === 403) {
          setShowBetaForm(true);
        } else {
          setError(t('auth:login.googleFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [googleLogin, navigate, t],
  );

  const handleGoogleLoginRef = useRef(handleGoogleLogin);
  useEffect(() => {
    handleGoogleLoginRef.current = handleGoogleLogin;
  }, [handleGoogleLogin]);

  useEffect(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
        callback: (response: any) => handleGoogleLoginRef.current(response),
      });

      const hidden = document.createElement('div');
      hidden.id = 'hidden-google-signup-button';
      hidden.style.display = 'none';
      document.body.appendChild(hidden);

      window.google.accounts.id.renderButton(hidden, { theme: 'outline', size: 'large', type: 'standard' });
      return () => hidden.remove();
    }
  }, []);

  const handleGoogleButtonClick = () => {
    const hiddenButton = document.querySelector('#hidden-google-signup-button div[role="button"]') as HTMLElement;
    if (hiddenButton) hiddenButton.click();
    else if (window.google) window.google.accounts.id.prompt();
  };

  if (showBetaForm) {
    return (
      <div style={{ minHeight: '100dvh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ width: '100%', maxWidth: 560 }}>
          <BetaAccessForm
            initialEmail={email}
            onSuccess={() => {
              setShowBetaForm(false);
              setError('');
            }}
          />
        </div>
      </div>
    );
  }

  const Side = (
    <div
      style={{
        padding: isMobile ? '28px 20px' : '40px 56px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: isMobile ? 20 : 0,
        background: 'var(--bg-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Link to="/login" className="nav-link">
          {t('auth:register.haveAccount', 'Have an account? Sign in →')}
        </Link>
      </div>

      <div>
        <div className="eyebrow" style={{ marginBottom: 14, color: 'var(--accent)' }}>
          {t('auth:register.eyebrowQuote', 'Build log · Apr 26')}
        </div>
        <blockquote
          style={{
            margin: 0,
            padding: 0,
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 30,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: 'var(--text)',
          }}
        >
          {t(
            'auth:register.testimonial',
            '“The first prompt put a playable build in front of me before I even finished my coffee.”',
          )}
        </blockquote>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, color: 'var(--text-5)', fontSize: 13 }}>
          <span style={{ width: 28, height: 28, borderRadius: 999, background: 'var(--surface-4)', display: 'inline-block' }} />
          <span>
            Mira Khan&nbsp;<span className="muted">— indie · "Pocket Atlas"</span>
          </span>
        </div>
      </div>

      <div />
    </div>
  );

  return (
    <div style={{ height: isMobile ? 'auto' : '100dvh', minHeight: '100dvh', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', background: 'var(--bg)' }}>
      <div
        style={{
          padding: isMobile ? '28px 20px' : '40px 56px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: isMobile ? 28 : 0,
          borderRight: isMobile ? 'none' : '1px solid var(--line)',
        }}
      >
        <Link to="/" className="brand" style={{ fontSize: 18 }}>
          <IconMark size={22} />
          {t('common:app.name', 'Entire')}
        </Link>

        {verificationStep ? (
          <div>
            <div className="eyebrow" style={{ marginBottom: 18 }}>
              {t('auth:register.verification.eyebrow', 'Verify your email')}
            </div>
            <h2 className="display tight" style={{ fontSize: 40, lineHeight: 1.05, margin: '0 0 14px' }}>
              {t('auth:register.verification.title', 'Check your inbox.')}
            </h2>
            <p className="muted-2" style={{ fontSize: 15, margin: '0 0 28px', maxWidth: 420, lineHeight: 1.5, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <IconMail />
              {t('auth:register.verification.subtitle', { email, defaultValue: `We sent a 6-digit code to ${email}.` })}
            </p>

            <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 380 }}>
              {error && (
                <div
                  style={{
                    padding: '10px 14px',
                    border: '1px solid var(--danger-line)',
                    background: 'var(--danger-wash)',
                    color: 'var(--danger)',
                    borderRadius: 10,
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              )}

              <div className="field">
                <label htmlFor="code">{t('auth:register.verification.codeLabel', 'Verification code')}</label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  className="input mono"
                  placeholder="000000"
                  style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.4em' }}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>

              <button
                className="btn primary lg"
                type="submit"
                disabled={isSubmitting || verificationCode.length !== 6}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                {isSubmitting
                  ? t('auth:register.verification.verifying', 'Verifying…')
                  : t('auth:register.verification.verifyButton', 'Verify and continue')}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                <button
                  type="button"
                  className="nav-link"
                  style={{ height: 'auto', padding: 0, color: 'var(--text-5)', fontSize: 12 }}
                  onClick={() => {
                    setVerificationStep(false);
                    setVerificationCode('');
                    setError('');
                  }}
                >
                  {t('auth:register.verification.back', '← Back')}
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  className="nav-link"
                  style={{ height: 'auto', padding: 0, color: resendCooldown > 0 ? 'var(--text-6)' : 'var(--accent)', fontSize: 12 }}
                >
                  {resendCooldown > 0
                    ? t('auth:register.verification.resendCooldown', { seconds: resendCooldown, defaultValue: `Resend in ${resendCooldown}s` })
                    : t('auth:register.verification.resendButton', 'Resend code')}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div>
            <div className="eyebrow" style={{ marginBottom: 18 }}>
              {t('auth:register.eyebrow', 'Create account')}
            </div>
            <h2 className="display tight" style={{ fontSize: 40, lineHeight: 1.05, margin: '0 0 14px' }}>
              {t('auth:register.title', 'Start your first game.')}
            </h2>
            <p className="muted-2" style={{ fontSize: 15, margin: '0 0 32px', maxWidth: 380, lineHeight: 1.5 }}>
              {t('auth:register.subtitle', 'Free during beta. One account holds every project.')}
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380 }}>
              {error && (
                <div
                  style={{
                    padding: '10px 14px',
                    border: '1px solid var(--danger-line)',
                    background: 'var(--danger-wash)',
                    color: 'var(--danger)',
                    borderRadius: 10,
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={handleGoogleButtonClick}
                disabled={isSubmitting}
                className="btn lg"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <IconGoogle />
                {t('auth:login.googleButton', 'Continue with Google')}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0' }}>
                <div className="hairline" style={{ flex: 1 }} />
                <span className="eyebrow">{t('auth:register.orText', 'or')}</span>
                <div className="hairline" style={{ flex: 1 }} />
              </div>

              <div className="field">
                <input
                  id="name"
                  type="text"
                  required
                  className="input"
                  placeholder={t('auth:register.namePlaceholder', 'Your name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="field">
                <input
                  id="email"
                  type="email"
                  required
                  className="input"
                  placeholder={t('auth:register.emailPlaceholder', 'you@studio.com')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <input
                  id="password"
                  type="password"
                  required
                  className="input"
                  placeholder={t('auth:register.passwordPlaceholder', 'Password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <button className="btn primary lg" type="submit" disabled={isSubmitting} style={{ width: '100%', justifyContent: 'center' }}>
                {isSubmitting
                  ? t('auth:register.creating', 'Creating account…')
                  : t('auth:register.createButton', 'Create account')}
              </button>
            </form>
          </div>
        )}

        <div />
      </div>

      {Side}
    </div>
  );
}
